import React from 'react';
import Loader from "../Loader/Loader";
import ErrorPage from "../Error/ErrorPage";
import {Collection} from "../../types/types";
import {getPublicUrl, useTimeout} from "../../common/helpers";
import {Box, Fade, useTheme} from "@mui/material";
import Gallery from "../Gallery/Gallery";

const defaultLocale = "en-US";

declare global {
    interface Window {
        dataLayer: Record<string, any>[];
    }
}

function App() {

    const theme = useTheme();

    const minLoadingScreenTimeMillis = 1500;

    const queryParameters = new URLSearchParams(window.location.search);
    const galleryIdentifier = queryParameters.get("gallery");

    const [collectionData, setCollectionData] = React.useState<Collection | null>(null);
    const [dataLoadingErrorCode, setDataLoadingErrorCode] = React.useState<number | null>(null);
    const [isMinLoadingTimeElapsed, setIsMinLoadingTimeElapsed] = React.useState<boolean>(false);
    const [selectedLocale, setSelectedLocale] = React.useState<string>(defaultLocale);
    
    React.useEffect(() => {
        async function getData() {
            if (galleryIdentifier && collectionData === null) {
                try {
                    const response = await fetch(
                        getPublicUrl(`${galleryIdentifier}/data.json?t=${(new Date()).getTime()}`),
                        {cache: "no-store"}
                    );
                    if (response.ok) {
                        const data = (await response.json()) as Collection;
                        document.title = data.collectionName || document.title;
                        setCollectionData(data);
                    } else {
                        setDataLoadingErrorCode(response.status);
                    }
                } catch (error: any) {
                    setDataLoadingErrorCode(0);
                }
            }
        }

        getData();
    }, [collectionData, galleryIdentifier]);

    useTimeout(() => {
        setIsMinLoadingTimeElapsed(true);
    }, minLoadingScreenTimeMillis);

    /**
     * Decides on whether there is an error when loading the gallery
     */
    const isErrorPresent = dataLoadingErrorCode !== null || galleryIdentifier === null;

    /**
     * Decides on whether the loader component should be displayed
     */
    const shouldLoaderBeDisplayed = !isMinLoadingTimeElapsed && (collectionData === null || !isErrorPresent);

    return (
        <Box className="App" sx={{backgroundColor: theme.palette.background.default, minHeight: "100%", width: "100%"}}>
            <Fade in={shouldLoaderBeDisplayed} timeout={shouldLoaderBeDisplayed ? 1 : 2000}>
                <Loader/>
            </Fade>
            {!shouldLoaderBeDisplayed &&
                (isErrorPresent ? <ErrorPage galleryIdentifier={galleryIdentifier} code={dataLoadingErrorCode}/> :
                    <Gallery collection={collectionData} locale={selectedLocale} setLocale={setSelectedLocale}/>)}
        </Box>
    );
}

export default App;